import React, { Component } from 'react';

class ReplyForm extends Component {
    constructor(props) {
        super(props);
        
        const { originalMessage } = props;
        const replySubject = originalMessage.subject?.startsWith('Re:') 
            ? originalMessage.subject 
            : `Re: ${originalMessage.subject || '(No Subject)'}`;
        
        // Create quoted original message
        const quotedBody = this.createQuotedBody(originalMessage);
        
        this.state = {
            subject: replySubject,
            body: quotedBody,
            sending: false
        };
    }

    createQuotedBody = (originalMessage) => {
        const fromName = originalMessage.from_name || originalMessage.from;
        const date = new Date(originalMessage.date).toLocaleString();
        
        // Get the original message text (prefer text over HTML)
        let originalText = '';
        if (originalMessage.text_body) {
            originalText = originalMessage.text_body;
        } else if (originalMessage.html_body) {
            // Convert HTML to readable text for quoting
            originalText = this.htmlToText(originalMessage.html_body);
        }
        
        // Create the quoted reply format
        const quotedText = originalText
            .split('\n')
            .map(line => `> ${line}`)
            .join('\n');
        
        return `\n\nOn ${date}, ${fromName} wrote:\n${quotedText}`;
    };

    htmlToText = (html) => {
        // Parsed into an inert document, never the live one.
        //
        // A div from document.createElement belongs to the active document, so
        // the fragment innerHTML builds inside it is live: <video>, <audio> and
        // <source> begin loading the moment they are created, fail, and run
        // their inline onerror -- in this origin, next to the token in
        // localStorage -- without the node ever being inserted anywhere. The
        // sandboxed iframe and DOMPurify in SandboxedHtmlBody.js are not on this
        // path; the only thing upstream is imap_helper.rb#sanitize_html, whose
        // handler pattern matches double-quoted values only and which strips
        // <img> but not the media elements. Verified in Chromium: four payloads
        // execute here and none do below.
        //
        // A DOMParser document has no browsing context, so nothing in it loads
        // and no handler runs. It is what DOMPurify parses with, for this reason.
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const body = doc.body;

        // Source, not prose. textContent counts the contents of these elements,
        // so without this a stylesheet or a script block is quoted back into the
        // reply as a wall of CSS or JS. Parsing into a document already moves a
        // leading <style> into <head>; removing them says so regardless of where
        // the parser put them.
        body.querySelectorAll('script, style, noscript, template').forEach(el => el.remove());

        // Handle common HTML elements for better text conversion
        const elements = body.querySelectorAll('*');

        // Convert block elements to add line breaks
        elements.forEach(el => {
            const tagName = el.tagName.toLowerCase();

            // Add line breaks after block elements
            if (['div', 'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
                el.appendChild(doc.createTextNode('\n'));
            }

            // Add line breaks around list items
            if (tagName === 'li') {
                el.appendChild(doc.createTextNode('\n'));
            }

            // Convert line breaks
            if (tagName === 'br') {
                el.parentNode.replaceChild(doc.createTextNode('\n'), el);
            }
        });

        // Get the text content and clean it up
        let text = body.textContent || '';
        
        // Clean up multiple consecutive newlines and spaces
        text = text
            .replace(/\n\s*\n\s*\n/g, '\n\n') // Max 2 consecutive newlines
            .replace(/[ \t]+/g, ' ') // Multiple spaces to single space
            .replace(/^\s+|\s+$/g, '') // Trim start/end whitespace
            .replace(/\n /g, '\n'); // Remove spaces at start of lines
        
        return text;
    };

    componentDidMount() {
        // Focus the textarea and position cursor at the beginning
        if (this.textareaRef) {
            this.textareaRef.focus();
            this.textareaRef.setSelectionRange(0, 0);
        }
    }

    handleSubjectChange = (e) => {
        this.setState({ subject: e.target.value });
    };

    handleBodyChange = (e) => {
        this.setState({ body: e.target.value });
    };

    handleSend = async (e) => {
        e.preventDefault();
        
        const { subject, body } = this.state;
        
        if (!body.trim()) {
            alert('Please enter a message body');
            return;
        }

        this.setState({ sending: true });
        
        try {
            await this.props.onSend(subject, body);
        } catch (error) {
            console.error('Error sending reply:', error);
        }
        
        this.setState({ sending: false });
    };

    render() {
        const { onCancel, originalMessage } = this.props;
        const { subject, body, sending } = this.state;

        return (
            <div className="reply-form">
                <h5>Reply to: {originalMessage.from}</h5>
                
                <form onSubmit={this.handleSend}>
                    <div className="form-group">
                        <label htmlFor="reply-subject">Subject:</label>
                        <input 
                            id="reply-subject"
                            type="text" 
                            className="form-control"
                            value={subject}
                            onChange={this.handleSubjectChange}
                            disabled={sending}
                        />
                    </div>
                    
                    <div className="form-group">
                        <label htmlFor="reply-body">Message:</label>
                        <textarea 
                            id="reply-body"
                            className="form-control"
                            rows="10"
                            value={body}
                            onChange={this.handleBodyChange}
                            placeholder="Type your reply here..."
                            disabled={sending}
                            required
                            ref={ref => this.textareaRef = ref}
                        />
                    </div>
                    
                    <div className="form-actions">
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            disabled={sending || !body.trim()}
                        >
                            {sending ? 'Sending...' : 'Send Reply'}
                        </button>
                        
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={onCancel}
                            disabled={sending}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }
}

export default ReplyForm;